/**
 * A nudge on the journey page while ordering is open: shown only when the
 * person holds a voucher and has not ordered yet. One quiet request; on any
 * failure there is simply no banner.
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ShoppingBag } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { formatSast } from '@/lib/staffOrder'
import { staffOrderService } from '@/services/api/staffOrderService'

export function StaffOrderBanner({ className }: { className?: string }) {
  const [closesAt, setClosesAt] = useState<string | null | undefined>(undefined)

  useEffect(() => {
    let cancelled = false
    staffOrderService
      .getCurrentSilent()
      .then((page) => {
        if (cancelled || !page) return
        const { round, allowance, order } = page
        if (round && allowance && round.status === 'open' && round.can_order && !order) {
          setClosesAt(round.closes_at)
        }
      })
      .catch(() => {
        // Silent by design: no banner.
      })
    return () => {
      cancelled = true
    }
  }, [])

  if (closesAt === undefined) return null
  const closes = formatSast(closesAt)

  return (
    <div
      className={cn(
        'flex flex-col gap-3 rounded-xl border border-future-blue-200 bg-white/90 px-4 py-3 shadow-sm sm:flex-row sm:items-center',
        className,
      )}
      role="status"
    >
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <ShoppingBag className="h-5 w-5 shrink-0 text-heritage-blue" />
        <p className="text-sm font-medium text-heritage-blue-700">
          Your staff apparel voucher is ready{closes ? ` — order by ${closes}` : ''}
        </p>
      </div>
      <Button asChild size="sm" className="shrink-0 self-start sm:self-auto">
        <Link to="/staff-order">Choose my items</Link>
      </Button>
    </div>
  )
}
